import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronsRight, Fingerprint } from 'lucide-react';
import toast from 'react-hot-toast';
import { signIn } from '../../lib/supabase';
import { biometricReady, unlockBiometric, forgetBiometric } from '../../utils/biometric';
import { BrandLogo } from './BrandLogo';
import { useDisplayName } from '../../hooks/useDisplayName';

// Phone app: the screen you see every time the app opens. Slide the gold button to the end to
// go in (to your dashboard if you are signed in, to the sign-in form if you are not).
//
// Background photo: drop a picture at public/brand/photos/welcome.jpeg and it appears behind the
// text automatically. Until then the screen shows a dark showroom-lighting gradient.

const greetingFor = (d) => {
  const h = d.getHours();
  return h < 12 ? 'Good Morning' : h < 18 ? 'Good Afternoon' : 'Good Evening';
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dateLine = (d) => `${DAYS[d.getDay()]}, ${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
const timeLine = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

const THUMB = 52;        // px, the gold button
const PAD = 4;           // px, space between the button and the track edge
const DONE_AT = 0.86;    // how far (0 to 1) counts as "slid all the way"

export const Welcome = ({ signedIn, loading = false, onEnter }) => {
  const { firstName } = useDisplayName();
  const [now, setNow] = useState(() => new Date());
  const [progress, setProgress] = useState(0);        // 0 to 1
  const [dragging, setDragging] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const track = useRef(null);
  const drag = useRef(null);                          // { startX, startProgress, range }
  const [bio, setBio] = useState(null);               // null = checking, true = fingerprint sign-in is on, false = not
  const [bioBusy, setBioBusy] = useState(false);
  const prompted = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);

  const range = () => Math.max(1, (track.current?.clientWidth || 300) - THUMB - PAD * 2);

  const finish = useCallback(() => {
    if (leaving) return;
    setProgress(1);
    setLeaving(true);
    setTimeout(onEnter, 380);
  }, [leaving, onEnter]);

  // Signed out, and fingerprint / face sign-in is turned on: ask for it instead of showing the slider.
  useEffect(() => {
    let off = false;
    if (signedIn) { setBio(false); return undefined; }
    biometricReady().then(ok => { if (!off) setBio(ok); });
    return () => { off = true; };
  }, [signedIn]);
  const useBio = !signedIn && bio === true;

  const tryBio = async () => {
    if (bioBusy || leaving) return;
    setBioBusy(true);
    let creds = null;
    try { creds = await unlockBiometric(); } catch { /* cancelled or not recognised: stay on this screen */ }
    if (!creds?.username) { setBioBusy(false); return; }
    const { error } = await signIn(creds.username, creds.password);
    if (error) {
      // The saved password no longer works (it was changed): forget it and fall back to the normal sign-in.
      await forgetBiometric();
      setBio(false);
      toast.error('Your saved sign-in is out of date. Please sign in with your password.');
      setBioBusy(false);
      return;
    }
    setBioBusy(false);
    finish();
  };

  useEffect(() => {
    if (useBio && !loading && !prompted.current) { prompted.current = true; tryBio(); }
  }, [useBio, loading]);   // eslint-disable-line react-hooks/exhaustive-deps

  const onDown = (e) => {
    if (leaving) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { startX: e.clientX, startProgress: progress, range: range() };
    setDragging(true);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    setProgress(Math.min(1, Math.max(0, d.startProgress + (e.clientX - d.startX) / d.range)));
  };
  const onUp = () => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    if (progress >= DONE_AT) finish(); else setProgress(0);
  };
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') { e.preventDefault(); finish(); }
  };

  const who = signedIn ? (firstName || '') : '';
  const offset = progress * range();

  return (
    <div className={`wl${leaving ? ' is-leaving' : ''}`} role="dialog" aria-label="Welcome">
      <div className="wl-bg" aria-hidden="true">
        <div className="wl-photo" style={{ backgroundImage: "url('/brand/photos/welcome.jpeg')" }} />
        <div className="wl-lights" />
        <div className="wl-floor" />
        <div className="wl-shade" />
      </div>

      <div className="wl-top">
        <BrandLogo variant="white" width={210} />
      </div>

      <div className={`wl-mid${loading ? ' is-loading' : ''}`}>
        <div className="wl-hello">{signedIn && who ? `${greetingFor(now)},` : 'Welcome'}</div>
        <div className="wl-name">{signedIn && who ? who : 'to your collection'}</div>
        <div className="wl-when">
          <span>{dateLine(now)}</span>
          <i aria-hidden="true" />
          <span>{timeLine(now)}</span>
        </div>
      </div>

      <div className={`wl-bottom${loading || (!signedIn && bio === null) ? ' is-hidden' : ''}`}>
        {useBio ? (
          <div className="wl-bio">
            <button type="button" className={`wl-bio-btn${bioBusy ? ' is-busy' : ''}`} onClick={tryBio} aria-label="Sign in with fingerprint or face">
              <Fingerprint size={38} strokeWidth={1.6} />
            </button>
            <div className="wl-bio-label">TAP TO SIGN IN</div>
            <button type="button" className="wl-bio-alt" onClick={finish}>Use email &amp; password</button>
          </div>
        ) : (
        <div className="wl-track" ref={track}>
          <span className="wl-label" style={{ opacity: Math.max(0, 1 - progress * 1.6) }}>
            {signedIn ? 'SLIDE TO ENTER' : 'SLIDE TO SIGN IN'}
          </span>
          <div
            className={`wl-thumb${dragging ? ' is-dragging' : ''}`}
            style={{ transform: `translateX(${offset}px)` }}
            role="slider"
            tabIndex={0}
            aria-label={signedIn ? 'Slide to enter' : 'Slide to sign in'}
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onKeyDown={onKey}
          >
            <ChevronsRight size={22} strokeWidth={2.2} />
          </div>
        </div>
        )}
      </div>
    </div>
  );
};
